import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useReducer, useEffect, useRef, useState, forwardRef, useImperativeHandle, } from 'react';
import { DragAndDropProvider } from '@sas/nova-commons/dnd/DragAndDropProvider';
import { TabBar } from '@sas/nova-commons/components/TabBar';
import { TransitionState } from './TransitionState';
import { PageTransition } from './PageTransition';
import { ReportSection } from '../../ReportSection';
import ReactFocusLock from 'react-focus-lock';
import { exposeTestId } from '../../../config';
import { useI18n } from '../../../locale/I18nContext';
import { useLatestRef, useOnUnmount } from '../../hooks';
import { getUriParameterBoolean } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { createUseTextLocalizer } from '@sas/nova-commons/locale/createUseTextLocalizer';
import { getFirstFocusable } from '@sas/nova-commons/util/focus';
// @ts-ignore
import novaTabBundle from '@sas/nova-commons/components/TabBar/NovaComponents-Tab-gui-icu.properties';
const fadeTransitionEnabled = getUriParameterBoolean('VASDK_FADETRANSITION', false);
const useTabTextLocalizer = createUseTextLocalizer(novaTabBundle);
export const ReportSections = forwardRef((props, ref) => {
    const [state, dispatch] = useReducer(TransitionState.reducer, null, () => TransitionState.init(props.currentSection));
    const latest = TransitionState.selectors.getLatest(state);
    if (latest !== props.currentSection) {
        dispatch(TransitionState.actions.createTransition(props.currentSection));
    }
    const currentIndex = props.sectionIds.indexOf(props.currentSection);
    const sections = TransitionState.selectors.getOrderedSections(state);
    const transitioning = TransitionState.selectors.transitioning(state);
    const onTransitionsComplete = useLatestRef(props.onTransitionsComplete);
    const renderedSections = useRef();
    useEffect(() => {
        if (!transitioning) {
            onTransitionsComplete.current();
        }
    }, [transitioning, onTransitionsComplete]);
    useOnUnmount(props.onTransitionsComplete);
    const [needsFocus, setNeedsFocus] = useState(false);
    const refPageTransition = useRef(null);
    const latestSectionRef = useRef(null);
    useImperativeHandle(ref, () => ({
        focusCurrentSection() {
            var _a;
            const latestSection = latestSectionRef.current;
            (_a = getFirstFocusable(latestSection !== null && latestSection !== void 0 ? latestSection : undefined)) === null || _a === void 0 ? void 0 : _a.focus();
        },
    }), []);
    useEffect(() => {
        var _a;
        // Comparing the activeElement to document.body is to catch the case where the user
        // has done some page linking then clicks on the root of the breadcrumb to return
        // to the first page. The breadcrumb goes away before the page is switched
        // so focus is lost. A more elegant solution would be to add a "page switch reason"
        // to the store.
        const lostFocus = document.activeElement === document.body;
        setNeedsFocus(transitioning && (lostFocus || !!((_a = refPageTransition.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))));
    }, [transitioning]);
    const promptHeaderHover = props.promptHeaderHover;
    const draggableTabs = props.tabsEditable;
    const localize = useI18n();
    const localizeTabText = useTabTextLocalizer();
    const { editState, onTabLabelChange } = useTabItemEditState({
        validate: (index, value) => {
            const isDuplicate = items.some((item, itemIndex) => {
                var _a;
                // don't test against the same item.
                if (itemIndex === index)
                    return false;
                // NOTE: nova-commons tests the strings with trim and locale unaware uppercase
                return ((_a = item.label) === null || _a === void 0 ? void 0 : _a.trim().toUpperCase()) === value.trim().toUpperCase();
            });
            if (isDuplicate) {
                return {
                    message: localizeTabText('tab.labelEdit.duplicateLabel.errorMessage.label', {
                        label: value,
                    }),
                    status: 'alert',
                };
            }
            return { message: undefined, status: undefined };
        },
        onCommit: (itemIndex, item, value, type) => {
            var _a;
            // nova-commons ignores empty values instead of returning a validation result for it
            // code in nova-commons for this is part of useTabItemsState.
            if (value !== '') {
                (_a = props.onTabLabelChange) === null || _a === void 0 ? void 0 : _a.call(props, itemIndex, item, value, type);
            }
        },
    });
    const sectionIds = [];
    const items = [];
    for (const id of props.sectionIds) {
        const section = props.sections[id];
        if (!(section === null || section === void 0 ? void 0 : section.hidden) && (section === null || section === void 0 ? void 0 : section.label)) {
            sectionIds.push(id);
            // NOTE: items.length will be the index of the element pushed on the next line.
            const isEditTab = items.length === (editState === null || editState === void 0 ? void 0 : editState.itemIndex);
            items.push(Object.assign(Object.assign({}, (isEditTab ? editState.result : {})), { label: section.label, menuSections: section.menuSections, hidden: section.hiddenStyle, allowDrag: draggableTabs, closable: section.closable, menuButtonTooltip: localize({
                    key: `ReportSections.menuButton.tooltip.fmt`,
                    args: {
                        label: section.label,
                    },
                }) }));
        }
    }
    const newTabButtonLabel = localize({ key: 'ReportSections.newPageButton.txt' });
    const hideNavigation = props.hideNavigation === true ||
        ((props.hideNavigation === 'auto' || props.hideNavigation === undefined) &&
            props.sectionIds.length <= 1);
    let tabBar = hideNavigation ? null : (_jsx(TabBar, { "data-testId": exposeTestId() ? 'reportPageTabs' : undefined, selectedIndex: currentIndex, items: items, onItemSelected: (event) => {
            const id = sectionIds[event.index];
            const section = props.sections[id];
            if (section) {
                props.onSectionChanged(section, id);
            }
        }, visualStyle: "standard", overflowMenuButtonDisplay: "onVisibleScrollbar", tokenOverrides: props.tabBarTokens, tabOverrides: props.tabTokens, editable: props.tabsEditable, allowDrop: draggableTabs, onTabLabelChange: onTabLabelChange, reorderTabs: props.onReorderTabs, addNewTabButtonProps: !props.tabsEditable || !props.onAddNewTab
            ? undefined
            : {
                onPress: props.onAddNewTab,
                ariaLabel: newTabButtonLabel,
                title: newTabButtonLabel,
            }, onTabMenuOpen: (itemIndex, item) => {
            var _a;
            (_a = props.onTabMenuOpen) === null || _a === void 0 ? void 0 : _a.call(props, { itemIndex, item });
        }, onTabMenuItemSelect: (itemIndex, item, location) => {
            var _a;
            (_a = props.onTabMenuItemSelect) === null || _a === void 0 ? void 0 : _a.call(props, { itemIndex, item, location });
        }, onDrop: props.onTabDrop, dropTypes: props.tabDropTypes, showCloseMenuOption: false, hideCloseAllTabOption: true, closeTabButtonTooltip: localize({ key: 'ReportSections.closeTab.tooltip.txt' }), onTabClose: (itemIndex) => {
            var _a;
            (_a = props.onTabClose) === null || _a === void 0 ? void 0 : _a.call(props, itemIndex);
        } }));
    if (draggableTabs) {
        tabBar = _jsx(DragAndDropProvider, Object.assign({ enableDragDrop: true }, { children: tabBar }));
    }
    return (_jsxs(Fragment, { children: [tabBar, _jsx(PageTransition, Object.assign({ ref: refPageTransition, transitioning: transitioning, onTransitionComplete: () => dispatch(TransitionState.actions.createTransitionComplete()), fadeTransition: fadeTransitionEnabled || props.fadeTransition }, { children: Object.keys(props.sections).map((id) => {
                    var _a;
                    const activeSections = (renderedSections.current = (_a = renderedSections.current) !== null && _a !== void 0 ? _a : new Set());
                    activeSections.add(props.currentSection);
                    const data = props.sections[id];
                    if (!activeSections.has(id) || !data) {
                        return undefined;
                    }
                    return (_jsx(ReactFocusLock, { as: ReportSection, ref: id === latest ? latestSectionRef : undefined, lockProps: { data, hideNavigation, promptHeaderHover, hide: !sections.includes(id) }, disabled: !needsFocus, noFocusGuards: true }, data.id));
                }) }), "sectionsContainer")] }));
});
function useTabItemEditState({ validate, onCommit, }) {
    const [editState, setEditState] = useState(undefined);
    const onTabLabelChange = (itemIndex, item, value, type) => {
        if (type === 'Cancel') {
            setEditState(undefined);
        }
        else if (type === 'Commit') {
            setEditState(undefined);
            const { status } = validate(itemIndex, value);
            // ignore validations that return 'warning' or 'alert'
            if (status === null || status === undefined) {
                onCommit === null || onCommit === void 0 ? void 0 : onCommit(itemIndex, item, value, type);
            }
        }
        else if (type === 'Change') {
            const result = validate(itemIndex, value);
            setEditState({ itemIndex, result });
        }
    };
    return { editState, onTabLabelChange };
}
